import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getEmployeeGovernance, type EmployeeGovernance } from '../api/client';
import { DateValue, Pending } from '../pages/org/shared';

/**
 * The governance view of one employee. Kept in its own component so the large,
 * working EmployeeDetail page needed only a tab entry rather than surgery.
 * Every section renders from the server aggregate, which applies RBAC — the
 * authority block comes back null when the caller may not see it.
 */
export function GovernancePanel({ employeeId }: { employeeId: string }) {
  const [data, setData] = useState<EmployeeGovernance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEmployeeGovernance(employeeId)
      .then((d) => { setData(d); setError(null); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <div className="panel"><div className="empty"><strong>Loading governance record…</strong></div></div>;
  if (error) return <div className="banner error">{error}</div>;
  if (!data) return null;

  const { organization: o, jobDescription: jd, workArrangement: wa, authority, employmentHistory, approvals, governance } = data;

  return (
    <div className="org-grid">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="panel">
          <div className="panel-head"><strong>Organization</strong></div>
          <dl className="org-jd-meta">
            <div><dt>Designation</dt><dd><Pending>{o.designation ?? undefined}</Pending></dd></div>
            <div><dt>Department</dt><dd><Pending>{o.department ?? undefined}</Pending></dd></div>
            <div><dt>Business unit</dt><dd><Pending>{o.businessUnit ?? undefined}</Pending></dd></div>
            <div><dt>Reports to</dt><dd>{o.reportsToId
              ? <Link className="record-link" to={`/employees/${o.reportsToId}`}>{o.reportsTo}</Link>
              : o.isTopAuthority ? <span>None — final organizational authority</span> : <Pending />}</dd></div>
          </dl>

          <h3 className="org-sub">Organization path</h3>
          <p className="mono" style={{ fontSize: 12 }}>{o.organizationPath.join('  ›  ')}</p>

          <h3 className="org-sub">Direct reports</h3>
          {o.directReports.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>None.</p> : (
            <ul className="org-plain-list">
              {o.directReports.map((r) => (
                <li key={r.id}>
                  <Link className="record-link" to={`/employees/${r.id}`}>{r.name}</Link>
                  <span className="muted" style={{ fontSize: 12 }}><Pending>{r.designation ?? undefined}</Pending></span>
                </li>
              ))}
            </ul>
          )}

          <h3 className="org-sub">Functional assignments</h3>
          {o.functionalAssignments.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>None recorded.</p> : (
            <ul className="org-plain-list">
              {o.functionalAssignments.map((f) => (
                <li key={f.area}>
                  <span>{f.area}{f.isPrimary && <span className="org-chip org-chip-quiet" style={{ marginLeft: 8 }}>primary</span>}</span>
                  <span className="muted" style={{ fontSize: 12 }}>{f.functionalManager ?? 'No functional manager'}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="muted org-footnote">
            A functional assignment says what someone works on. It is not a reporting line and confers no authority.
          </p>
        </div>

        <div className="panel">
          <div className="panel-head">
            <strong>Job description</strong>
            <Link className="record-link" to="/org/job-descriptions">Open library</Link>
          </div>
          {!jd ? (
            <div className="empty">
              <strong>No job description</strong>
              <p>Responsibilities, authority and limits are undocumented for this person.</p>
            </div>
          ) : (
            <>
              <dl className="org-jd-meta">
                <div><dt>Version</dt><dd className="mono">{jd.version}</dd></div>
                <div><dt>Status</dt><dd><span className={`org-chip org-chip-${jd.status}`}>{jd.status}</span></dd></div>
                <div><dt>Effective</dt><dd><DateValue value={jd.effectiveDate} /></dd></div>
                <div><dt>Management approval</dt><dd><DateValue value={jd.approvedAt} /></dd></div>
                <div><dt>Employee acknowledgement</dt><dd>{jd.acknowledged ? <DateValue value={jd.acknowledgedAt} /> : <Pending />}</dd></div>
                <div><dt>Versions on file</dt><dd className="mono">{jd.versions}</dd></div>
              </dl>
              <h3 className="org-sub">Responsibilities</h3>
              {jd.responsibilities?.length
                ? <ul className="org-bullets">{jd.responsibilities.map((r) => <li key={r}>{r}</li>)}</ul>
                : <p><Pending /></p>}
              <h3 className="org-sub">KPIs</h3>
              {jd.kpis?.length
                ? <ul className="org-bullets">{jd.kpis.map((k) => <li key={k}>{k}</li>)}</ul>
                : <p><Pending /></p>}
            </>
          )}
        </div>

        <div className="panel">
          <div className="panel-head"><strong>Employment history</strong></div>
          {employmentHistory.length === 0 ? (
            <div className="empty"><strong>No recorded organizational changes</strong></div>
          ) : (
            <ol className="org-timeline">
              {employmentHistory.map((h, i) => (
                <li key={`${h.type}-${h.on}-${i}`}>
                  <span className="org-timeline-dot" />
                  <span>
                    <strong>{h.type.replace(/_/g, ' ')}</strong>
                    <span className="muted"> {h.from ?? '—'} → {h.to ?? '—'}</span>
                    <div className="muted" style={{ fontSize: 12 }}>
                      <DateValue value={h.on} />{h.reason ? ` · ${h.reason}` : ''}
                    </div>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <div className="org-side">
        <div className="panel">
          <div className="panel-head"><strong>Work arrangement</strong></div>
          {!wa ? <div className="empty"><strong>Not recorded</strong></div> : (
            <>
              <dl className="org-jd-meta">
                <div><dt>Current</dt><dd><Pending>{wa.current ?? undefined}</Pending></dd></div>
                <div><dt>Request</dt><dd>
                  {wa.requestStatus === 'none'
                    ? <span className="muted">None open</span>
                    : <span className={`org-chip org-chip-${wa.requestStatus}`}>{wa.requestStatus.replace(/_/g, ' ')}</span>}
                </dd></div>
                <div><dt>Requested</dt><dd>{wa.requested ?? <span className="muted">—</span>}</dd></div>
                <div><dt>Effective from</dt><dd><DateValue value={wa.effectiveFrom} /></dd></div>
                <div><dt>Review date</dt><dd><DateValue value={wa.reviewDate} /></dd></div>
              </dl>
              {wa.requestStatus !== 'none' && wa.requestStatus !== 'approved' && (
                <div className="banner warn" style={{ margin: '10px 0 0' }}>
                  Requested, not approved. The arrangement is unchanged until the workflow completes.
                </div>
              )}
              <h3 className="org-sub">History</h3>
              {wa.history.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>No changes recorded.</p> : (
                <ul className="org-plain-list">
                  {wa.history.map((h, i) => (
                    <li key={i}><span>{h.from} → {h.to}</span><span className="mono" style={{ fontSize: 12 }}>{String(h.on).slice(0, 10)}</span></li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div className="panel">
          <div className="panel-head"><strong>Authority</strong></div>
          {!authority ? (
            <div className="empty">
              <strong>Withheld</strong>
              <p>You do not hold the permission to view delegated authority.</p>
            </div>
          ) : (
            <>
              <h3 className="org-sub">Approval authority</h3>
              {authority.approvalAuthority?.length
                ? <ul className="org-bullets">{authority.approvalAuthority.map((a) => <li key={a}>{a}</li>)}</ul>
                : <p><Pending /></p>}
              <h3 className="org-sub">Restrictions</h3>
              {authority.restrictions?.length
                ? <ul className="org-bullets">{authority.restrictions.map((r) => <li key={r}>{r}</li>)}</ul>
                : <p><Pending /></p>}
              <h3 className="org-sub">Delegated to them</h3>
              {authority.delegatedToThem.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>None.</p> : (
                <ul className="org-plain-list">
                  {authority.delegatedToThem.map((d, i) => (
                    <li key={i}>
                      <span>{d.authorityType.replace(/_/g, ' ')}
                        <div className="muted" style={{ fontSize: 12 }}>
                          {d.monetaryLimit != null ? `${d.currency} ${d.monetaryLimit.toLocaleString('en-IN')}` : 'no limit set'} · {d.startsOn.slice(0, 10)} → {d.endsOn.slice(0, 10)}
                        </div>
                      </span>
                      <span className={`org-chip org-chip-${d.status}`}>{d.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div className="panel">
          <div className="panel-head"><strong>Governance</strong></div>
          <ul className="org-plain-list">
            <li><span>Joining date</span>{governance.joiningDateConfirmed
              ? <span className="org-chip org-chip-active">confirmed</span>
              : <Pending />}</li>
            <li><span>Data classification</span><span className={`org-chip org-chip-${governance.dataClassification === 'real' ? 'active' : 'quiet'}`}>{governance.dataClassification}</span></li>
            <li><span>Approvals submitted</span><span className="org-count">{approvals.submitted}</span></li>
            <li><span>Awaiting their decision</span><span className={`org-count${approvals.awaitingThem ? ' org-count-warn' : ''}`}>{approvals.awaitingThem}</span></li>
            <li><span>Pending</span><span className="org-count">{approvals.pending}</span></li>
            <li><span>Approved</span><span className="org-count">{approvals.approved}</span></li>
            <li><span>Rejected</span><span className="org-count">{approvals.rejected}</span></li>
          </ul>
          <p className="muted org-footnote">
            Audit history for this person is in the Security &amp; Audit Trail, which is the only complete record.
          </p>
        </div>
      </div>
    </div>
  );
}
